import React from 'react';
import {
  Backdrop,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

import withShipment from '../../withShipment';
import AuthUtil from '../../redux/AuthUtil';
import { EMPLOYEE_SESSION_V2 } from './Server/endpoints';
import { customersSelector } from '../../redux/selectors/customer';
import { employeesSelector } from '../../redux/selectors/auth';
import { manageBonusSelector } from '../../redux/selectors/settings';
import PdfBody from '../employeeSession/shared/PdfBody';
import generateSessionPdf, {
  suggestedFilename,
} from '../employeeSession/shared/generateSessionPdf';
import RenderPurchaseOrderPDFPreview from '../transaction/RenderPurchaseOrderPDFPreview';
import Utils from '../Utils/Utils';

// Preview opens at 100% so the full page width is visible on narrow screens;
// the user zooms in from there. (react-pdf's min usable scale is 1.)
const PREVIEW_MIN_ZOOM = 1;

/**
 * SessionReportPdf — reusable driver for the new employee-session PDF.
 *
 * This replaces the legacy server-rendered (PDFBox) shift / points / ticket /
 * bonus / raffle reports. It renders the shared `PdfBody` off-screen, rasterizes
 * it with `generateSessionPdf` (the exact machinery the Employee Session screen
 * uses), then shows the result in a full-screen preview with Download / Print /
 * zoom — matching the "preview first, then print or download" flow these screens
 * had before.
 *
 * Drop it (hidden) into any screen and drive it with the `trigger` prop: bump
 * `trigger` (e.g. a counter) to open a report. It resolves the employee's latest
 * clock session server-side, so callers only need `userId` + `locationId`.
 *
 * Props:
 *   type        'shift' | 'points' | 'ticket' | 'bonus' | 'raffle'
 *   userId      employee whose most-recent shift to report on
 *   locationId  location context
 *   locationName FALLBACK label only. The authoritative name comes back on the
 *               getSessionReports payload (`locationName`), resolved server-side
 *               from the Company record. The prop is sourced from the persisted
 *               redux `selectedLocationName`, which is only refreshed at login —
 *               so it goes stale after a rename and is empty on accounts that
 *               never had it stamped. Never use it when the server sent one.
 *   trigger     changing this (truthy) value starts a generation
 *   onError     (message) => void — surfaced to the host screen's feedback bar
 */
class SessionReportPdf extends React.Component {
  state = {
    busy: false,
    sessionData: null, // set only while the off-screen PdfBody is captured
    previewOpen: false,
    pdfBuffer: null, // ArrayBuffer kept for download/print
    pdfViewData: null, // separate copy handed to react-pdf (it may detach it)
    session: null,
    // Server-resolved location name, kept past the off-screen render so the
    // download/print filename uses the same value the header was drawn with.
    resolvedLocationName: null,
    scale: PREVIEW_MIN_ZOOM,
  };

  pdfRef = React.createRef();

  // Current location name for the header + filename: the server value wins,
  // the persisted redux prop is only a fallback for older backends.
  locationName(data) {
    const fromServer = data && data.locationName;
    return fromServer || this.props.locationName || '';
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.trigger &&
      prevProps.trigger !== this.props.trigger &&
      !this.state.busy
    ) {
      this.generate();
    }
  }

  buildSession(data) {
    return {
      id: data.sessionNumber || '—',
      employee: data.employeeName,
      clockIn: data.clockIn,
      clockOut: data.clockOut,
      hoursWorked: data.hoursWorked,
    };
  }

  generate = async () => {
    const { userId, locationId, onError } = this.props;
    if (this.state.busy) return;
    if (!userId || !locationId) {
      onError && onError('Missing employee or location for this report.');
      return;
    }
    this.setState({ busy: true });
    try {
      // Load the whole shift payload in one round-trip. clockId is omitted so
      // the server falls back to the employee's latest clock session — exactly
      // what the old preview endpoints resolved server-side.
      const res = await AuthUtil.postRequest(
        EMPLOYEE_SESSION_V2.LOAD_SESSION_REPORTS,
        { userId, locationId }
      );
      const data = res && res.data;
      if (!data || data.success === false) {
        onError &&
          onError(
            (data && data.message) ||
              'No shift session found for this employee.'
          );
        this.setState({ busy: false });
        return;
      }

      // Mount the off-screen PdfBody with the freshly loaded data, then wait a
      // couple of frames so layout + images settle before html2canvas captures.
      await new Promise((resolve) => {
        this.setState({ sessionData: data }, () => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
      });

      const element = this.pdfRef.current;
      if (!element) {
        this.setState({ busy: false, sessionData: null });
        return;
      }

      const buffer = await generateSessionPdf({ element });
      if (!buffer) {
        this.setState({ busy: false, sessionData: null });
        return;
      }

      this.setState({
        busy: false,
        sessionData: null, // off-screen render no longer needed
        pdfBuffer: buffer,
        // react-pdf may transfer/detach the buffer it renders, so hand it an
        // independent copy and keep the original for download/print.
        pdfViewData: new Uint8Array(buffer.slice(0)),
        session: this.buildSession(data),
        resolvedLocationName: this.locationName(data),
        previewOpen: true,
        scale: PREVIEW_MIN_ZOOM,
      });
    } catch (e) {
      // The server signals "nothing to report" with HTTP 400 plus a
      // specific reason ("No clock session found for this employee",
      // "userId required"). Axios rejects on 4xx, so those responses never
      // reach the `success === false` branch above — the message has to be
      // read off the error. Falling straight through to the generic line
      // threw away the one piece of information the user needed.
      //
      // The generic text is kept only for a genuine failure with no server
      // message: a dropped connection, or a 5xx with no body.
      const serverMessage =
        e && e.response && e.response.data && e.response.data.message;
      onError &&
        onError(serverMessage || 'Failed to generate report. Please try again.');
      this.setState({ busy: false, sessionData: null });
    }
  };

  fileName() {
    return suggestedFilename({
      type: this.props.type,
      session: this.state.session,
      locationName: this.state.resolvedLocationName || this.props.locationName,
    });
  }

  handleDownload = () => {
    if (!this.state.pdfBuffer) return;
    Utils.handlePdfAction(this.state.pdfBuffer, 'download', this.fileName());
  };

  handlePrint = () => {
    if (!this.state.pdfBuffer) return;
    Utils.handlePdfAction(this.state.pdfBuffer, 'print', this.fileName());
  };

  setZoom = (delta) => {
    this.setState((s) => ({
      scale: Math.min(Math.max(s.scale + delta, PREVIEW_MIN_ZOOM), 3),
    }));
  };

  closePreview = () => {
    this.setState({
      previewOpen: false,
      pdfBuffer: null,
      pdfViewData: null,
      session: null,
      resolvedLocationName: null,
    });
  };

  render() {
    const { type, customers, employees, manageBonusList } = this.props;
    const { busy, sessionData, previewOpen, pdfViewData, scale } = this.state;
    return (
      <React.Fragment>
        {sessionData && (
          // Off-screen render — kept in the live DOM so layout sizes are real,
          // positioned far off the viewport so the user never sees it.
          <div
            style={{
              position: 'fixed',
              left: -10000,
              top: 0,
              width: 720,
              background: '#fff',
              padding: 24,
              fontFamily: 'sans-serif',
              pointerEvents: 'none',
            }}
          >
            <div ref={this.pdfRef}>
              <PdfBody
                type={type}
                session={this.buildSession(sessionData)}
                sessionData={sessionData}
                locationName={this.locationName(sessionData)}
                customers={customers}
                employees={employees}
                manageBonusList={manageBonusList}
              />
            </div>
          </div>
        )}

        <Dialog
          fullScreen
          open={previewOpen}
          onClose={this.closePreview}
          PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '6px 12px',
              background: '#0F172A',
              color: '#fff',
            }}
          >
            <Tooltip title="Back">
              <IconButton onClick={this.closePreview} sx={{ color: '#fff' }}>
                <KeyboardBackspaceIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download PDF">
              <IconButton onClick={this.handleDownload} sx={{ color: '#fff' }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print PDF">
              <IconButton onClick={this.handlePrint} sx={{ color: '#fff' }}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom Out">
              <IconButton
                onClick={() => this.setZoom(-0.25)}
                sx={{ color: '#fff' }}
              >
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <span style={{ minWidth: 48, textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </span>
            <Tooltip title="Zoom In">
              <IconButton
                onClick={() => this.setZoom(0.25)}
                sx={{ color: '#fff' }}
              >
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
          </div>
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              background: '#525659',
              padding: 16,
            }}
          >
            {/* Block container + auto side-margins: when the page is wider than
                the viewport (zoomed in), auto margins collapse to 0 so the page
                left-aligns at scroll origin and BOTH edges are reachable; when it
                fits, the margins center it. Flex `justify-content:center` here
                clips the left edge and can't scroll to it. */}
            {pdfViewData && (
              <div style={{ width: 'fit-content', margin: '0 auto' }}>
                <RenderPurchaseOrderPDFPreview data={pdfViewData} scale={scale} />
              </div>
            )}
          </div>
        </Dialog>

        <Backdrop
          open={busy}
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 100,
            color: '#fff',
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  customers: customersSelector(state),
  employees: employeesSelector(state),
  manageBonusList: manageBonusSelector(state),
});

export default withShipment({ mapStateToProps }, SessionReportPdf);
